import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

export type ResolvedSx =
  | SystemStyleObject<Theme>
  | ((theme: Theme) => SystemStyleObject<Theme>);

const isThemeFunction = (
  value: SxProps<Theme>,
): value is (theme: Theme) => SystemStyleObject<Theme> => {
  return typeof value === "function";
};

const isSystemStyleObject = (
  value: SxProps<Theme>,
): value is SystemStyleObject<Theme> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const flattenSx = (
  value: SxProps<Theme> | undefined,
): ResolvedSx | undefined => {
  if (!value) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return (theme) => {
      return value.reduce<SystemStyleObject<Theme>>((acc, entry) => {
        if (!entry) {
          return acc;
        }

        if (typeof entry === "function") {
          return { ...acc, ...entry(theme) };
        }

        return { ...acc, ...entry };
      }, {});
    };
  }

  if (isThemeFunction(value)) {
    return value;
  }

  if (isSystemStyleObject(value)) {
    return {
      ...value,
    };
  }

  return undefined;
};

export const mergeSx = (
  base: SystemStyleObject<Theme>,
  next: SxProps<Theme> | undefined,
): ResolvedSx => {
  const resolvedNext = flattenSx(next);

  if (!resolvedNext) {
    return base;
  }

  if (typeof resolvedNext === "function") {
    return (theme) => {
      return {
        ...base,
        ...resolvedNext(theme),
      };
    };
  }

  return {
    ...base,
    ...resolvedNext,
  };
};
